import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { HxProductInfoService, HxUiLabelPipe, isoDate, PagedList, ProductInfoModel, ProductInfoType, SaleProductModel } from 'hx-services';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { TranslocoPipe } from '@jsverse/transloco';
import { ProductPriceLabelPipe } from './product-price-label.pipe';
import { ADDITIONAL_NOMENCLATURE_CATEGORY_ID } from './product-info-select.constants';

/**
 * Component search product info by search text
 */
@Component({
    selector: 'hx-product-info-select',
    templateUrl: './product-info-select.component.html',
    styleUrls: ['./product-info-select.component.css'],
    encapsulation: ViewEncapsulation.None,
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => HxProductInfoSelectComponent), // replace name as appropriate
        multi: true
      }
    ],
  imports: [NgSelectComponent, FormsModule, NgLabelTemplateDirective, NgOptionTemplateDirective, TranslocoPipe, HxUiLabelPipe, ProductPriceLabelPipe]
})
export class HxProductInfoSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() multiple = false;
  @Input() clearAfterSelect = false;
  @Input() types: ProductInfoType[] = [];
  @Input() isPurchasable = false;
  @Input() brandId?: number;
  @Input() brandIds?: number[];
  @Input() storeId?: number;
  /**
   * When true and storeId is set, appends the current price in parentheses
   * (e.g. "Свеча 1 (450 ₸)") for products in Доп. номенклатура (categoryId=4).
   * Default false — all other usages of this component are unaffected.
   */
  @Input() showPrice = false;
  @Output() selectChange = new EventEmitter<ProductInfoModel | ProductInfoModel[] | undefined>();
  selected: number | number[] | undefined;
  disabled = false;
  options: ProductInfoModel[] = [];
  itemLoading = false;
  itemInput$ = new Subject<string>();
  /** Price map: productInfoId → price value. Populated only when showPrice=true. */
  priceMap = new Map<number, number>();
  private $destroyed = new Subject<void>();

  constructor(
    private productInfoService: HxProductInfoService,
  ) {
  }

  ngOnInit() {
    this.loadItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['showPrice'] || changes['storeId']) && this.showPrice && this.storeId) {
      this.loadPriceMap();
    }
  }

  writeValue(val: number | number[] | undefined): void {
    this.selected = val;
    if (val) {
      if (Array.isArray(val)) {
        this.productInfoService.getProductInfoByIds(val).subscribe(list => this.options = list);
      } else {
        this.productInfoService.getProductInfoByIds([val]).subscribe(list => this.options = list);
      }
    }
  }

  registerOnChange(fn: (val: number | number[] | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(_fn: () => void): void {
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onChange(_val: number | number[] | undefined) {
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  private loadItems() {
    this.itemInput$.pipe(
      takeUntil(this.$destroyed),
      debounceTime(500),
      distinctUntilChanged(),
      filter(term => {
        const isOk = (term ?? '').trim() !== '';
        this.itemLoading = isOk;
        return isOk;
      }),
      switchMap(term => {
        const brandIdList: number[] = [];
        if (this.brandId) {
          brandIdList.push(this.brandId);
        }
        if (this.brandIds && this.brandIds.length) {
          brandIdList.concat(this.brandIds);
        }
        return this.productInfoService.getProductInfos({
          brandIds: brandIdList,
          searchText: term,
          types: this.types,
          storeId: this.storeId,
          purchasable: this.isPurchasable
        }).pipe(
          finalize(() => this.itemLoading = false),
          catchError(() => of({list: [], count: 0} as PagedList<ProductInfoModel>)), // empty list on error
        );
      })
    ).subscribe(searchResult => this.options = searchResult.list);
  }

  private loadPriceMap(): void {
    const today = isoDate(Date.now());
    this.productInfoService.getProductInfosForSale({
      storeId: this.storeId!,
      categoryIds: [ADDITIONAL_NOMENCLATURE_CATEGORY_ID],
      fromDate: today!,
    }).pipe(
      takeUntil(this.$destroyed),
    ).subscribe((saleProducts: SaleProductModel[]) => {
      const map = new Map<number, number>();
      for (const sp of saleProducts) {
        const basePrice = sp.prices?.find(p => p.amount === 1);
        if (basePrice && basePrice.value > 0) {
          map.set(sp.id, basePrice.value);
        }
      }
      this.priceMap = map;
    });
  }

  onModelChanged(val: number | number[] | undefined) {
    this.onChange(val);
    this.selectChange.emit(this.getSelectedOptions());
    if (this.clearAfterSelect) {
      setTimeout(() => {
        this.selected = undefined;
        this.onChange(undefined);
      });
    }
  }

  private getSelectedOptions(): ProductInfoModel | ProductInfoModel[] | undefined {
    if (this.multiple) {
      if (Array.isArray(this.selected)) {
        const ids: number[] = this.selected;
        return this.options.filter(p => ids.includes(p.id));
      } else {
        return [];
      }
    } else {
      if (this.selected) {
        return this.options.find(p => p.id === this.selected);
      } else {
        return undefined;
      }
    }
  }
}
